import {
  EXPANDED_EDITOR_FOOTER,
  EXPANDED_EDITOR_ACTIONS,
} from "../expanded-editor-footer.js";
import { useState } from "react";
import {
  ControlPlanParamsSchema,
  widgetTargetKey,
  type ControlPlanRow,
  type ControlPlanParams,
} from "@playbookai/widget-schemas";
import { useFieldEditSession } from "../field-edit-session.js";
import { WidgetPersonField } from "../person-field.js";
import type { WidgetChangeOptions } from "../types.js";
import { usePeopleSource } from "../user-directory.js";
import { Button } from "../../atoms/Button/index.js";
import { Input } from "../../atoms/Input/index.js";
import { Textarea } from "../../atoms/Textarea/index.js";
import { FormField } from "../../molecules/FormField/index.js";
import { Modal } from "../../molecules/Modal/index.js";
import { isCompleteControl } from "./control-plan-helpers.js";

export function ControlDetails({
  row,
  data,
  isNew,
  readOnly,
  onDirty,
  onSave,
  onCancel,
  onDelete,
}: {
  row: ControlPlanRow;
  data: ControlPlanParams;
  isNew: boolean;
  readOnly: boolean;
  onDirty: () => void;
  /** With `grantViewerIfMissing`, a promise that settles with the save. */
  onSave: (
    row: ControlPlanRow,
    options?: WidgetChangeOptions,
  ) => void | Promise<void>;
  onCancel: () => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState<ControlPlanRow>();
  const [baseline, setBaseline] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const people = usePeopleSource();
  const shown = draft ?? row;
  // A person picked in this draft, not the one already saved: Save may add
  // them as Viewer (d4 — forms grant on Save, and Cancel adds nobody).
  const pickedNew =
    !!shown.responsibleUserId &&
    shown.responsibleUserId !== row.responsibleUserId;
  /**
   * PB-4923 AC5. This editor holds a draft behind an explicit Save, so the
   * unprotected window is longer here than anywhere with auto-commit — a
   * half-filled control can sit on screen for minutes.
   *
   * A NEW row leases nothing: it has no server-side row yet, so a claim on
   * `rows[<fresh-uuid>]/item` names a target no operation could ever address.
   */
  const target = (field: string): string | undefined =>
    isNew
      ? undefined
      : widgetTargetKey([
          { kind: "item", collection: "rows", itemId: row.id },
          { kind: "prop", name: field },
        ]);
  const itemLease = useFieldEditSession(target("item"));
  const responsibleLease = useFieldEditSession(target("responsible"));
  const cadenceLease = useFieldEditSession(target("cadence"));
  const triggerLease = useFieldEditSession(target("trigger"));
  // The save's own optimistic echo is not someone else's change.
  const conflict =
    !saving && !!draft && !isNew && JSON.stringify(row) !== baseline;
  const atLimit = isNew && data.rows.length >= 50;
  const valid =
    isCompleteControl(shown) &&
    ControlPlanParamsSchema.safeParse({ rows: [shown] }).success;
  const update = (patch: Partial<ControlPlanRow>) => {
    if (!draft) setBaseline(JSON.stringify(row));
    setDraft({ ...shown, ...patch });
    setSaveError(null);
    onDirty();
  };
  const save = () => {
    if (readOnly || saving || conflict || atLimit || !valid) return;
    const saved = onSave(
      {
        ...shown,
        item: shown.item.trim(),
        responsible: shown.responsible.trim(),
        cadence: shown.cadence.trim(),
        trigger: shown.trigger.trim(),
      },
      pickedNew ? { grantViewerIfMissing: true } : undefined,
    );
    if (!(saved instanceof Promise)) return;
    // A refused save keeps the draft and says why under Responsible.
    setSaving(true);
    saved.catch((reason: unknown) => {
      setSaving(false);
      setSaveError(
        reason instanceof Error && reason.message
          ? reason.message
          : "Edit not saved",
      );
    });
  };
  return (
    <>
      <form
        className="@container flex min-h-full flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <h3 className="text-sm font-semibold text-pb-text">
          {isNew ? "New control" : "Control details"}
        </h3>
        <FormField label="What to monitor" required>
          <Input
            value={shown.item}
            maxLength={255}
            disabled={readOnly}
            {...itemLease}
            onChange={(event) => update({ item: event.target.value })}
          />
        </FormField>
        <div className="grid gap-4 @lg:grid-cols-2">
          {people ? (
            <WidgetPersonField
              source={people}
              label="Responsible"
              required
              text={shown.responsible}
              userId={shown.responsibleUserId}
              timing="save"
              pendingGrant={pickedNew}
              error={saveError}
              readOnly={readOnly}
              {...responsibleLease}
              onChange={(next) =>
                update({
                  responsible: next.text,
                  responsibleUserId: next.userId,
                })
              }
            />
          ) : (
            <FormField label="Responsible" required>
              <Input
                value={shown.responsible}
                maxLength={255}
                disabled={readOnly}
                {...responsibleLease}
                onChange={(event) =>
                  update({ responsible: event.target.value })
                }
              />
            </FormField>
          )}
          <FormField label="Cadence" required>
            <Input
              value={shown.cadence}
              maxLength={255}
              disabled={readOnly}
              {...cadenceLease}
              onChange={(event) => update({ cadence: event.target.value })}
            />
          </FormField>
        </div>
        <FormField
          label="Action trigger"
          required
          hint="Describe when action is needed and what to do."
        >
          <Textarea
            value={shown.trigger}
            maxLength={500}
            rows={5}
            disabled={readOnly}
            {...triggerLease}
            onChange={(event) => update({ trigger: event.target.value })}
          />
        </FormField>
        {conflict && (
          <p role="alert" className="text-sm text-pb-text">
            This control changed while you were editing. Cancel to reload the
            latest version.
          </p>
        )}
        {atLimit && (
          <p role="alert" className="text-sm text-pb-text">
            The plan has reached its limit of 50 controls.
          </p>
        )}
        {!readOnly && (
          <div className={EXPANDED_EDITOR_FOOTER}>
            {!isNew && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-pb-red-text"
                disabled={!!draft}
                onClick={() => setConfirmDelete(true)}
              >
                Delete control
              </Button>
            )}
            <div className={EXPANDED_EDITOR_ACTIONS}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={
                  (!draft && !isNew) || !valid || conflict || atLimit || saving
                }
              >
                Save changes
              </Button>
            </div>
          </div>
        )}
      </form>
      <Modal
        open={confirmDelete && !readOnly}
        onClose={() => setConfirmDelete(false)}
        title="Delete this control?"
        description="This removes the monitoring rule from the control plan."
        size="sm"
        placement="center"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!readOnly) onDelete();
              }}
            >
              Delete control
            </Button>
          </>
        }
      >
        <p className="text-sm text-pb-text">{row.item || "Untitled control"}</p>
      </Modal>
    </>
  );
}
